import { Node, mergeAttributes } from "@tiptap/core";

// A board copied from the interactive board tool, drawn again in the answer. Like MermaidDiagram,
// only its data is persisted (data-board-state: the spec the board is built from, the values the
// student set, the part of the plane shown, the size it had) and the board itself is drawn
// client-side, here in the editor and in RichContentDisplay's read-only hydrateStatic(). It is
// drawn still - nothing in it moves - and "play" makes the same board one that can be dragged.
// JSXGraph and the board code are Examin's own files, loaded the first time a board is drawn and
// shared by every board on the page. A board a teacher made with a script has no spec (the script
// stays in the tool's isolated frame), so it is shown as the values the student set.

interface BoardData {
  v?: number;
  spec?: unknown;
  values?: unknown[];
  view?: number[];
  size?: { w: number; h: number };
  text?: string;
  image?: string;
  /** How a board in a question is used: "reference" (look, zoom and pan, nothing moves), "explore" (drag and
   *  play, nothing is kept) or "task" (the student works it, its values saved with the answer). A board
   *  with none - one copied from the tool into an answer - is shown still, with a play button. */
  mode?: string;
}

const MAX_WIDTH = 560;
let counter = 0;
let loading: Promise<void> | null = null;

function addScript(path: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = new URL(path, document.baseURI).href;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`${path} did not load`));
    document.head.appendChild(script);
  });
}

export function ensureBoardLibrary(): Promise<void> {
  if (!loading) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = new URL("vendor/jsxgraph/jsxgraph.css", document.baseURI).href;
    document.head.appendChild(link);
    loading = addScript("vendor/jsxgraph/jsxgraphcore.js")
      .then(() => addScript("board-core.js"))
      .catch((error) => { loading = null; throw error; });
  }
  return loading;
}

const hebrew = () => (document.documentElement.lang || "").toLowerCase().startsWith("he") || document.dir === "rtl";
const label = (he: string, en: string) => (hebrew() ? he : en);

// Where a board stands on its host, so it can be taken down when drawn again or when its block goes.
const drawn = new WeakMap<HTMLElement, any>();

function takeDown(host: HTMLElement): void {
  const board = drawn.get(host);
  drawn.delete(host);
  try { if (board) (window as any).JXG.JSXGraph.freeBoard(board); } catch { /* already gone */ }
}

// A box has a size only once the page has laid it out; a board needs it to be made.
function whenSized(box: HTMLElement): Promise<boolean> {
  return new Promise((resolve) => {
    let frames = 0;
    const check = () => {
      if (box.clientWidth > 0) return resolve(true);
      if (++frames > 60) return resolve(false);
      requestAnimationFrame(check);
    };
    check();
  });
}

export interface BoardBlockOptions {
  /** Draggable, with a button back to the still board. */
  live?: boolean;
  /** A task board, set in a question for the student to work: always draggable, no play button, and
   *  what the student sets is reported through onChange. */
  task?: boolean;
  /** What was set on the board, in place of the values the block was copied with. */
  values?: unknown[];
  onChange?: (values: string) => void;
  /** Shown as its author sees it, in the editor: still, with a mark of what it is for. */
  author?: boolean;
  /** In the editor: a button to open the graph in its panel to change it. */
  onEdit?: () => void;
}

const roundTo = (value: number) => Math.round(value * 1000) / 1000;

function describeValues(values: unknown[]): string {
  return (values as any[])
    .map((v) => (v.kind === "slider" ? `${v.name} = ${roundTo(v.v)}` : `${v.name} = (${roundTo(v.x)}, ${roundTo(v.y)})`))
    .join(", ");
}

export async function renderBoardBlock(host: HTMLElement, raw: string, options?: BoardBlockOptions): Promise<void> {
  const task = Boolean(options?.task);
  let data0: BoardData = {};
  try { data0 = JSON.parse(raw || "{}"); } catch { /* handled below */ }
  const how = options?.author ? undefined : data0.mode;
  // A board set for the student to explore is draggable at once, with no play button.
  const explore = !task && !options?.live && how === "explore";
  const reference = !task && !options?.live && how === "reference";
  const live = Boolean(options?.live) || task || explore;
  let data: BoardData;
  try { data = JSON.parse(raw || "{}"); } catch { host.replaceChildren(); return; }

  const stage = host.querySelector<HTMLElement>(":scope > .bsb-stage");
  if (stage) takeDown(stage);
  host.replaceChildren();
  host.setAttribute("dir", "ltr");

  const foot = document.createElement("div");
  foot.className = "bsb-foot";
  const text = document.createElement("span");
  text.className = "bsb-text";
  text.textContent = data.text || "";
  // A task board shows what the student has set as it is now, not as the board was copied.
  if (options?.values) text.textContent = describeValues(options.values);

  // A board a teacher wrote as a script (in the editor's graph panel): its code runs in a frame with no origin of its own, the
  // way the board tool runs it, so it reaches neither this page nor the student's answers. It is a drawing to look at (or, set to
  // explore, to play with): never a task.
  const script = (data as any).script;
  if (!data.spec && typeof script === "string" && script.trim()) {
    const frameHost = document.createElement("div");
    frameHost.className = "bsb-stage";
    frameHost.id = `bsb-script-${++counter}`;
    frameHost.style.cssText = "display:block; flex:none; min-height:240px;";   // a block in a flex host must not shrink away
    const frame = document.createElement("iframe");
    frame.className = "bd-frame";
    frame.style.cssText = "display:block; width:100%; height:100%; border:0;";
    frame.setAttribute("sandbox", "allow-scripts");
    frame.title = label("גרף", "Graph");
    frameHost.append(frame);
    host.append(frameHost);
    const ratioOf = data.size && data.size.w > 0 && data.size.h > 0 ? data.size.h / data.size.w : 2 / 3;
    try {
      if (!(await whenSized(frameHost)) || !host.isConnected) return;
      frameHost.style.height = `${Math.max(240, Math.round(frameHost.clientWidth * ratioOf))}px`;
      const tool: any = await import(/* @vite-ignore */ new URL("board-tool.js", document.baseURI).href);
      await tool.show(frameHost.id, { mode: "script", script, lock: how !== "explore" }, { error: label("שגיאה בגרף", "The graph has an error"), load: label("הגרף לא נטען", "The graph could not be loaded") }, "", true);
    } catch { /* a frame that cannot be built leaves an empty box */ }
    return;
  }

  if (!data.spec) {
    // A script's board: nothing to draw from here, only what was set on it.
    // Its picture, when it has one - an image of this site only.
    const address = data.image ? new URL(data.image, location.href) : null;
    if (address && address.origin === location.origin) {
      const picture = document.createElement("img");
      picture.className = "bsb-image";
      picture.alt = "";
      picture.src = address.href;
      host.append(picture);
    }
    const note = document.createElement("span");
    note.className = "bsb-note";
    note.textContent = label("לוח אינטראקטיבי", "Interactive board") + (data.text ? ": " : "");
    foot.append(note, text);
    host.append(foot);
    return;
  }

  const box = document.createElement("div");
  box.className = "bsb-stage";
  box.id = `bsb-${++counter}`;
  const ratio = data.size && data.size.w > 0 && data.size.h > 0 ? data.size.h / data.size.w : 2 / 3;
  host.append(box);

  const play = document.createElement("button");
  play.type = "button";
  play.className = "bsb-play";
  play.title = live ? label("חזרה לתצוגה קבועה", "Back to the still board") : label("הפעלת הלוח", "Play with the board");
  play.setAttribute("aria-label", play.title);
  play.setAttribute("aria-pressed", live ? "true" : "false");
  play.innerHTML = `<i class="bi ${live ? "bi-pause-fill" : "bi-play-fill"}" aria-hidden="true"></i>`;
  play.addEventListener("mousedown", (event) => event.preventDefault());
  play.addEventListener("click", () => { void renderBoardBlock(host, raw, { live: !live, values: options?.values, author: options?.author, onEdit: options?.onEdit }); });
  foot.append(text);
  if (!task && !explore && !reference) {
    // What the board is for, when its author or a teacher checking sees it still.
    const mark = data.mode === "task" ? label("משימה לתלמיד", "Task for the student")
      : data.mode === "explore" ? label("לוח לחקירה", "Board to explore")
      : data.mode === "reference" ? label("רפרנס", "Reference") : "";
    if (mark) {
      const badge = document.createElement("span");
      badge.className = "bsb-note";
      badge.textContent = mark;
      foot.prepend(badge);
    }
    // A graph made in the editor's own panel can be opened there again; one copied from the tool cannot.
    if (options?.onEdit && (data as any).cfg) {
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "bsb-play bsb-edit";
      edit.title = label("עריכת הגרף", "Edit the graph");
      edit.setAttribute("aria-label", edit.title);
      edit.innerHTML = `<i class="bi bi-pencil" aria-hidden="true"></i>`;
      edit.addEventListener("mousedown", (event) => event.preventDefault());
      edit.addEventListener("click", () => options.onEdit!());
      foot.append(edit);
    }
    foot.append(play);
  }
  host.append(foot);

  try {
    await ensureBoardLibrary();
    if (!(await whenSized(box)) || !host.isConnected) return;
    box.style.height = `${Math.max(120, Math.round(box.clientWidth * ratio))}px`;
    const core = (window as any).ExaminBoard;
    const board = core.build(box.id, data.spec, { view: data.view, static: !live && !reference });
    core.restore(board, options?.values ?? data.values ?? []);
    // A reference is looked at, zoomed and panned; its points and sliders stay where they were set.
    if (reference) core.lock(board);
    drawn.set(box, board);
    if (task && options?.onChange) {
      // What the student has set, reported when a drag ends or a slider settles.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const report = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const values = core.movable(board);
          text.textContent = core.texts(board).join(", ");
          options.onChange!(JSON.stringify(values));
        }, 250);
      };
      board.on("up", report);
    }
    // A box that is resized - a narrow window - keeps the part of the plane it shows.
    new ResizeObserver(() => {
      if (!drawn.has(box) || box.clientWidth <= 0) return;
      box.style.height = `${Math.max(120, Math.round(box.clientWidth * ratio))}px`;
      board.resizeContainer(box.clientWidth, box.clientHeight, true, false);
    }).observe(box);
  } catch (error) {
    // A board that cannot be drawn leaves what was set on it, as text; it never takes the answer down.
    console.warn("board: not drawn", error);
    box.remove();
    play.remove();
  }
}

export function disposeBoardBlock(host: HTMLElement): void {
  const stage = host.querySelector<HTMLElement>(":scope > .bsb-stage");
  if (stage) takeDown(stage);
}

export const BoardState = Node.create({
  name: "boardState",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      data: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-board-state") || "",
        renderHTML: (attrs: { data: string }) => ({ "data-board-state": attrs.data }),
      },
    };
  },

  // By its attribute and not its class: a paste has its classes stripped before it is parsed.
  parseHTML() {
    return [{ tag: "div[data-board-state]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "board-state-block", contenteditable: "false" })];
  },

  addNodeView() {
    return ({ node, getPos }) => {
      const dom = document.createElement("div");
      // Asks the editor to open this graph in its panel, with where it stands.
      const onEdit = () => dom.dispatchEvent(new CustomEvent("examin-board-edit", {
        bubbles: true,
        detail: { pos: getPos(), data: dom.getAttribute("data-board-state") },
      }));
      dom.className = "board-state-block";
      dom.setAttribute("data-board-state", node.attrs.data);
      dom.setAttribute("contenteditable", "false");
      void renderBoardBlock(dom, node.attrs.data, { author: true, onEdit });

      return {
        dom,
        selectNode: () => dom.classList.add("is-selected"),
        deselectNode: () => dom.classList.remove("is-selected"),
        update: (updatedNode) => {
          if (updatedNode.type.name !== "boardState") return false;
          if (updatedNode.attrs.data !== dom.getAttribute("data-board-state")) {
            dom.setAttribute("data-board-state", updatedNode.attrs.data);
            void renderBoardBlock(dom, updatedNode.attrs.data, { author: true, onEdit });
          }
          return true;
        },
        destroy: () => disposeBoardBlock(dom),
      };
    };
  },
});
